import React, { useMemo, useState } from 'react';
import { useQuery } from 'react-query';

import { SearchOutlined } from '@ant-design/icons';
import { Form, Modal } from 'antd';
import styled from 'styled-components';

import { getAllCommunities } from 'apis/CommunityAPI';
import { getHoldings } from 'apis/HoldingAPI';
import { getUsers } from 'apis/UserAPI';
import Button from 'components/lib/Button';
import Checkbox from 'components/lib/Checkbox';
import GradientDivider from 'components/lib/GradientDivider';
import Input from 'components/lib/Input';
import Select from 'components/lib/Select';
import TextArea from 'components/lib/TextArea';
import { PAGINATION_PAGE_SIZE } from 'constants/pagination';
import { useAppSelector } from 'store/hook';
import { CompanyType } from 'types/company';
import { GroupType } from 'types/groups';
import { HoldingType } from 'types/holding';
import { UserActorType } from 'types/user';
import { useDebounce } from 'utils/hooks/useDebounce';

import { COMPANY_COMMUNITY_RELATION_TYPE } from '../../../constants/company';

const Footer = styled.div`
  margin-top: 20px;
  padding: 16px 30px;
  display: flex;
  justify-content: flex-end;
  border-top: 1px solid var(--line-gray);
`;

const Label = styled.div`
  font-size: 14px;
  margin-right: 10px;
  font-family: var(--font-bold);
  color: var(--text-primary);
  min-width: 90px;
  display: flex;
  justify-content: flex-end;
  align-content: center;
  align-items: center;
`;

const Item = styled.div`
  display: flex;
  align-content: center;
  vertical-align: center;
  gap: 8px;
`;

const StyledModal = styled(Modal)`
  &&& {
    .ant-modal-content {
      padding: 0px;
      line-height: 1;
      border-radius: 1px;
      user-select: none;
    }
    .ant-modal-body {
      padding: 0px;
    }
  }
`;

const Header = styled.div`
  padding: 16px 30px;
  font-size: 20px;
  font-family: var(--font-bold);
  border-bottom: 1px solid var(--line-gray);
`;

const Container = styled.div`
  display: flex;
  overflow-y: auto;
  max-height: 700px;
  max-width: 600px;
  width: 600px;
  min-width: 350px;
  flex-direction: column;
  padding: 20px 30px 0px 30px;
  gap: 8px;
`;

const MenuItemLabel = styled.div`
  display: flex;
  gap: 4px;
`;

interface Props {
  open: boolean;
  group?: GroupType;
  handleClose: () => void;
  onSubmit: (item: GroupType) => Promise<void>;
  title: string;
}

const GroupsCreateEditModal = ({ open, group, handleClose, onSubmit, title }: Props) => {
  const { companies } = useAppSelector((state) => state.global);

  const [form] = Form.useForm();
  const [userSearch, setUserSearch] = useState('');
  const [communitySearch, setCommunitySearch] = useState('');

  const companyId = Form.useWatch('company_id', form);
  const holdingId = Form.useWatch('holding_id', form);

  const userDebouncedSearch = useDebounce(userSearch, 300);
  const communityDebouncedSearch = useDebounce(communitySearch, 300);

  const companiesOptions = (companies || []).map((company: CompanyType) => ({
    value: company.id,
    name: company.name,
    label: company.name,
  }));

  const { data: userSearchOptions } = useQuery({
    queryKey: ['users', userDebouncedSearch, companyId, holdingId],
    queryFn: () =>
      getUsers({
        search: userDebouncedSearch,
        companies: companyId ? [companyId] : undefined,
        holdings: holdingId ? [holdingId] : undefined,
        page_size: PAGINATION_PAGE_SIZE.MODERATE,
      }),
    select: (data) =>
      (data?.results ?? []).map((user: UserActorType) => ({
        label: user?.full_name?.length ? user.full_name : user.email || user.username,
        value: user.id,
      })),
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  const { data: holdingOptions } = useQuery({
    queryKey: ['holdings'],
    queryFn: () => getHoldings(),
    select: (data) => {
      return (data || []).map((holding: HoldingType) => ({
        value: holding.id,
        label: holding.name,
      }));
    },
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  const { data: communitySearchOptions } = useQuery({
    queryKey: ['communities', communityDebouncedSearch, companyId],
    queryFn: () =>
      getAllCommunities({
        companies: companyId ? [companyId] : undefined,
        companies_relation: COMPANY_COMMUNITY_RELATION_TYPE.COMMUNITY,
        search: communityDebouncedSearch,
        page_size: PAGINATION_PAGE_SIZE.MODERATE,
      }),
    select: (data) =>
      data?.results.map((community: any) => ({
        label: (
          <MenuItemLabel>
            <span>{community.comunity_name}</span>
            <span>-</span>
            <span style={{ color: 'var(--text-secondary)' }}>{community.company.name}</span>
          </MenuItemLabel>
        ),
        value: community.id,
      })),
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  const userOptions = useMemo(() => {
    const groupUserOptions = group
      ? group.users.map((user) => {
          const userLabel = user?.first_name?.length
            ? `${user.first_name} ${user.last_name}`
            : user.email || user.username;

          return { label: userLabel, value: user.id };
        })
      : [];

    const apiUserOptions = userSearchOptions || [];

    return [...new Map([...groupUserOptions, ...apiUserOptions].map((item) => [item.value, item])).values()];
  }, [group, userSearchOptions]);

  const communityOptions = useMemo(() => {
    const groupCommunityOptions = group
      ? group.communities.map((community) => ({
          label: (
            <MenuItemLabel>
              <span>{community.comunity_name}</span>
              <span>-</span>
              <span style={{ color: 'var(--text-secondary)' }}>{community.company.name}</span>
            </MenuItemLabel>
          ),
          value: community.id,
        }))
      : [];

    const apiCommunityOptions = communitySearchOptions || [];

    return [...new Map([...groupCommunityOptions, ...apiCommunityOptions].map((item) => [item.value, item])).values()];
  }, [group, communitySearchOptions]);

  const showCompanySelector = !group?.primary && !group?.holding;
  const showOwnerSelector = !group?.primary && !group?.company;
  const disabledCompanySelector = Boolean(group?.company) || Boolean(group?.holding) || holdingId;
  const disabledHoldingSelector = Boolean(group?.holding) || Boolean(group?.company) || companyId;
  const disabledCommunitiesSelector = Boolean(group?.company) && group?.primary;

  return (
    <StyledModal open={open} width="fit-content" footer={null} onCancel={handleClose} destroyOnClose>
      <Header>{title}</Header>
      <Form
        form={form}
        initialValues={
          group
            ? {
                ...group,
                company_id: group.company?.id,
                holding_id: group.holding?.id,
                users_ids: group.users.map((user) => user.id),
                communities_ids: group.communities.map((c) => c.id),
              }
            : {
                name: '',
                description: '',
                active: true,
                company_id: null,
                holding_id: null,
                users_ids: [],
                communities_ids: [],
              }
        }
        onFinish={onSubmit}
      >
        <Container>
          <Item>
            <Label>Name</Label>
            <Form.Item
              name="name"
              style={{ width: '100%', maxWidth: '600px' }}
              rules={[{ required: true, message: 'Please enter group name' }]}
            >
              <Input placeholder="Name" />
            </Form.Item>
          </Item>
          <Item>
            <Label>Description</Label>
            <Form.Item name="description" style={{ width: '100%', maxWidth: '600px' }}>
              <TextArea style={{ resize: 'vertical' }} rows={2} placeholder="Enter a description" />
            </Form.Item>
          </Item>
          <Item>
            <Label>Active</Label>
            <Form.Item name="active" rules={[{ required: true }]} valuePropName="checked">
              <Checkbox className={'green'} />
            </Form.Item>
          </Item>
          {(showCompanySelector || showOwnerSelector) && <GradientDivider />}
          {showCompanySelector && (
            <Item>
              <Label>Company</Label>
              <Form.Item name="company_id" style={{ width: '100%', maxWidth: '600px', overflow: 'hidden' }}>
                <Select
                  className="rectangular"
                  showSearch
                  allowClear
                  suffixIcon={<SearchOutlined />}
                  disabled={disabledCompanySelector}
                  style={{ borderRadius: 0 }}
                  filterOption={(input, option) => option?.name?.toLowerCase().includes(input.toLowerCase())}
                  notFoundContent={'No results found.'}
                  options={companiesOptions}
                  onSelect={(value) =>
                    form.setFieldsValue({
                      company_id: value,
                      holding_id: null,
                    })
                  }
                  dropdownStyle={{
                    padding: 0,
                    borderRadius: 0,
                    border: '1px solid var(--line-gray)',
                    boxShadow: '0 2px 4px 0 rgba(0, 0, 0, 0.1)',
                  }}
                />
              </Form.Item>
            </Item>
          )}

          {showOwnerSelector && (
            <Item>
              <Label>Owner</Label>
              <Form.Item name="holding_id" style={{ width: '100%', maxWidth: '600px', overflow: 'hidden' }}>
                <Select
                  className="rectangular"
                  showSearch
                  allowClear
                  disabled={disabledHoldingSelector}
                  suffixIcon={<SearchOutlined />}
                  style={{ borderRadius: 0 }}
                  filterOption={false}
                  notFoundContent={'No results found.'}
                  options={holdingOptions}
                  onSelect={(value) =>
                    form.setFieldsValue({
                      holding_id: value,
                      company_id: null,
                    })
                  }
                  dropdownStyle={{
                    padding: 0,
                    borderRadius: 0,
                    border: '1px solid var(--line-gray)',
                    boxShadow: '0 2px 4px 0 rgba(0, 0, 0, 0.1)',
                  }}
                />
              </Form.Item>
            </Item>
          )}
          {(showCompanySelector || showOwnerSelector) && <GradientDivider />}
          <Item>
            <Label>Users</Label>
            <Form.Item name="users_ids" style={{ width: '100%', maxWidth: '600px', overflow: 'hidden' }}>
              <Select
                mode="multiple"
                className="rectangular"
                showSearch
                allowClear
                suffixIcon={<SearchOutlined />}
                style={{ borderRadius: 0 }}
                onSearch={(value) => setUserSearch(value)}
                filterOption={false}
                notFoundContent={null}
                options={userOptions}
                dropdownStyle={{
                  padding: 0,
                  borderRadius: 0,
                  border: '1px solid var(--line-gray)',
                  boxShadow: '0 2px 4px 0 rgba(0, 0, 0, 0.1)',
                }}
              />
            </Form.Item>
          </Item>
          <Item style={{ marginBottom: 12 }}>
            <Label>Communities</Label>
            <Form.Item name="communities_ids" style={{ width: '100%', maxWidth: '600px', overflow: 'hidden' }}>
              <Select
                disabled={disabledCommunitiesSelector}
                mode="multiple"
                className="rectangular"
                showSearch
                allowClear
                suffixIcon={<SearchOutlined />}
                style={{ borderRadius: 0 }}
                onSearch={(value) => setCommunitySearch(value)}
                filterOption={false}
                notFoundContent={null}
                options={communityOptions}
                dropdownStyle={{
                  padding: 0,
                  borderRadius: 0,
                  border: '1px solid var(--line-gray)',
                  boxShadow: '0 2px 4px 0 rgba(0, 0, 0, 0.1)',
                }}
              />
            </Form.Item>
          </Item>
        </Container>
        <Footer>
          <Form.Item>
            <Button type={'primary'} htmlType="submit" style={{ marginRight: '10px' }}>
              Save
            </Button>
            <Button onClick={handleClose}>Cancel</Button>
          </Form.Item>
        </Footer>
      </Form>
    </StyledModal>
  );
};

export default GroupsCreateEditModal;
